import { useState, useEffect } from 'react'

// Cache « stale-while-revalidate » partage pour les lectures publiques du portail.
//
// Objectif (qualite percue type app native) : une donnee deja vue s'affiche
// INSTANTANEMENT (depuis le cache memoire), sans skeleton ni flicker, puis est
// revalidee en silence en arriere-plan. Les cles `persist` sont aussi gardees en
// localStorage : au demarrage d'une nouvelle session, le dernier etat connu
// s'affiche immediatement (warm start) le temps de la revalidation.
//
// Volontairement minimal (pas de dependance type react-query) : un Map memoire
// + une couche localStorage optionnelle.

const mem = new Map() // key -> data (derniere valeur connue)
const LS_PREFIX = 'lyannaj_swr:'
// Abonnes par cle : tous les useCachedFetch d'une meme cle se resynchronisent
// quand setCached ecrit (ex. pastille wallet du header + page Wallet — v0.73.4).
const listeners = new Map() // key -> Set<fn(data)>

function notify(key, data) {
  const subs = listeners.get(key)
  if (subs) subs.forEach((fn) => fn(data))
}

function readLS(key) {
  try {
    const v = localStorage.getItem(LS_PREFIX + key)
    return v ? JSON.parse(v) : undefined
  } catch {
    return undefined
  }
}

function writeLS(key, data) {
  try {
    localStorage.setItem(LS_PREFIX + key, JSON.stringify(data))
  } catch {
    /* quota depasse / mode prive : on garde au moins le cache memoire */
  }
}

/** Valeur en cache (memoire, puis localStorage si persist) ou undefined. */
export function getCached(key, { persist = false } = {}) {
  if (mem.has(key)) return mem.get(key)
  if (persist) {
    const v = readLS(key)
    if (v !== undefined) {
      mem.set(key, v)
      return v
    }
  }
  return undefined
}

/** Ecrit en cache (memoire + localStorage si persist) et previent les abonnes. */
export function setCached(key, data, { persist = false } = {}) {
  mem.set(key, data)
  if (persist) writeLS(key, data)
  notify(key, data)
}

/**
 * Hook de lecture stale-while-revalidate.
 * @param {string} key cle de cache (typiquement l'endpoint)
 * @param {() => Promise<any>} fetcher fonction de chargement
 * @param {{ persist?: boolean, enabled?: boolean }} opts
 * @returns {{ data: any, loading: boolean }} data = valeur immediate (cache) ou
 *   undefined ; loading = true uniquement au TOUT PREMIER chargement (aucun cache).
 */
export function useCachedFetch(key, fetcher, { persist = false, enabled = true } = {}) {
  const initial = enabled ? getCached(key, { persist }) : undefined
  const [data, setData] = useState(initial)
  const [loading, setLoading] = useState(initial === undefined && enabled)

  useEffect(() => {
    if (!enabled) return
    let alive = true
    // Abonnement : toute ecriture setCached(key) (par ce hook ou un autre
    // composant) resynchronise cette instance — plus de copies divergentes.
    const onUpdate = (d) => {
      if (!alive) return
      setData(d)
      setLoading(false)
    }
    let subs = listeners.get(key)
    if (!subs) {
      subs = new Set()
      listeners.set(key, subs)
    }
    subs.add(onUpdate)

    const cached = getCached(key, { persist })
    if (cached !== undefined) {
      setData(cached)
      setLoading(false)
    } else {
      setLoading(true)
    }
    Promise.resolve(fetcher())
      .then((d) => {
        if (!alive) return
        setCached(key, d, { persist }) // notify() met a jour ce hook et les autres
      })
      .catch(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      subs.delete(onUpdate)
      if (subs.size === 0) listeners.delete(key)
    }
    // fetcher volontairement hors deps (identite changeante) ; on re-fetch sur key/enabled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled])

  return { data, loading }
}
